import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '../../api/client';
import { useAuthStore } from '../../stores/authStore';
import { Button, Input, Card, CardBody } from '../../components/ui';
import { MALAYSIAN_STATES } from '../../lib/constants';

interface RegisterForm {
  email: string;
  password: string;
  confirmPassword: string;
  full_name: string;
  contact_number: string;
  state: string;
  referral_code: string;
}

export function RegisterPage() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegisterForm>();

  const password = watch('password');

  // Pre-fill referral code from URL ?ref=CODE
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setValue('referral_code', refCode.toUpperCase());
    }
  }, [searchParams, setValue]);

  const onSubmit = async (data: RegisterForm) => {
    setLoading(true);
    setError('');

    const { data: result, error } = await authApi.register({
      email: data.email,
      password: data.password,
      full_name: data.full_name,
      contact_number: data.contact_number,
      state: data.state,
      referral_code: data.referral_code || undefined,
    });

    setLoading(false);

    if (error) {
      setError(error);
      return;
    }

    if (result) {
      setAuth(result.user, result.token);
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img src="/logo.png" alt="Review Like a Pro" className="w-12 h-12" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-500">
            Sign in
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          <CardBody>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  {error}
                </div>
              )}

              <Input
                label="Full Name"
                {...register('full_name', {
                  required: 'Full name is required',
                  minLength: {
                    value: 2,
                    message: 'Name must be at least 2 characters',
                  },
                })}
                error={errors.full_name?.message}
              />

              <Input
                label="Email address"
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={errors.email?.message}
              />

              <Input
                label="Contact Number (WhatsApp preferred) *"
                {...register('contact_number', {
                  required: 'Contact number is required',
                  minLength: { value: 8, message: 'Please enter a valid contact number' },
                })}
                placeholder="e.g., 012-345 6789"
                error={errors.contact_number?.message}
              />
              <p className="-mt-3 text-xs text-gray-500">
                Payouts are made to your Touch 'n Go wallet linked to this number. Please make sure it's correct and also reachable on WhatsApp for any updates.
              </p>

              <div>
                <label className="label">State</label>
                <select
                  {...register('state', { required: 'Please select your state' })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                  defaultValue=""
                >
                  <option value="" disabled>Select your state…</option>
                  {MALAYSIAN_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {errors.state && (
                  <p className="mt-1 text-sm text-red-600">{errors.state.message}</p>
                )}
              </div>

              <Input
                label="Password"
                type="password"
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters',
                  },
                })}
                error={errors.password?.message}
              />

              <Input
                label="Confirm Password"
                type="password"
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (value) => value === password || 'Passwords do not match',
                })}
                error={errors.confirmPassword?.message}
              />

              <Input
                label="Referral Code (optional)"
                {...register('referral_code')}
                placeholder="Enter referral code if you have one"
              />

              <Button type="submit" className="w-full" loading={loading}>
                Create account
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
